/**
 * EventCard.js — Halleck Vineyard event card.
 *
 * OVERRIDES: src/components/base/EventCard.js
 * REASON: Halleck events are a plain WHITE card (gold glow) — cover image on top,
 *   a header block (title / date / time / location / sub_title), then a footer
 *   with a full-width "Learn more" pill and a price + copper CTA row. Matches the
 *   live createEvents builder + .event_card CSS (same white-card family as the
 *   wine / membership cards). Same DOM as the Goose event card; themed copper in
 *   halleckvineyard/styles.css.
 *
 * INTERFACE: render(ev) → HTMLElement, identical signature to base.
 *
 * Reference: halleck_vineyard/script.js createEvents.
 */

import { emit ,isChatDisabled} from "../../../core/state.js";

const CAL_ICON =
  '<svg viewBox="0 0 13 15" width="13" height="14" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.83 0.5v2.67M9.17 0.5v2.67M0.5 5.83h12M1.83 1.83h9.34A1.33 1.33 0 0 1 12.5 3.17v9.33a1.33 1.33 0 0 1-1.33 1.33H1.83A1.33 1.33 0 0 1 0.5 12.5V3.17a1.33 1.33 0 0 1 1.33-1.34z"/></svg>';

export default class EventCard {
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-card ws-rich-event";

    const openDetail = () => {
      if(isChatDisabled()) return;
      const label = ev.message || `Tell me more about ${ev.title || "this event"}`;
      emit("ws-prompt-select", { label, value: label, uniqueId: ev.unique_id });
    };

    // Cover image on top (live .event-cover-image), clickable → detail view.
    if (ev.cover_image_link) {
      const head = document.createElement("div");
      head.className = "ws-card-head";
      head.style.cursor = "pointer";
      head.addEventListener("click", openDetail);
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = ev.cover_image_link;
      img.alt = ev.title || "Event";
      img.loading = "lazy";
      head.appendChild(img);
      card.appendChild(head);
    }

    const inner = document.createElement("div");
    inner.className = "ws-rich-event-inner";

    // Header block (live .event-header): title, date, time, location, then the
    // sub_title as the description line (live createEvents uses sub_title here).
    const header = document.createElement("div");
    header.className = "ws-event-header";
    const addLine = (cls, text) => {
      if (!text) return;
      const p = document.createElement("p");
      p.className = cls;
      p.textContent = text;
      header.appendChild(p);
    };
    addLine("ws-event-title", ev.title);
    addLine("ws-event-date", ev.date);
    addLine("ws-event-time", ev.time);
    addLine("ws-event-location", ev.location);
    addLine("ws-event-desc", ev.sub_title);
    inner.appendChild(header);

    // Footer (live .event-footer): full-width learn-more pill, then a row with
    // the price block + copper CTA.
    const footer = document.createElement("div");
    footer.className = "ws-event-footer";

    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${CAL_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    footer.appendChild(learn);

    const row = document.createElement("div");
    row.className = "ws-event-footer-row";
    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-event-price-block";
    if (ev.price) {
      const price = document.createElement("p");
      price.className = "ws-card-price-amount";
      price.textContent = ev.price;
      priceBlock.appendChild(price);
    }
    if (ev.price_unit) {
      const unit = document.createElement("p");
      unit.className = "ws-card-price-unit";
      unit.textContent = ev.price_unit;
      priceBlock.appendChild(unit);
    }
    row.appendChild(priceBlock);

    if (ev.cta_link) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta";
      cta.href = ev.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = ev.cta_text || "Reserve";
      row.appendChild(cta);
    }
    footer.appendChild(row);
    inner.appendChild(footer);

    card.appendChild(inner);
    return card;
  }
}
